import { CollisionWorld, createRng, pointSegmentDistance, type Obstacle } from '@jianghu/shared';
import type { StructureDef, TreeKind, ZoneDef } from './types.js';

export interface TreeInstance {
  kind: TreeKind;
  x: number;
  z: number;
  scale: number;
  rot: number;
}

export interface ZoneLayout {
  zone: ZoneDef;
  obstacles: Obstacle[];
  trees: TreeInstance[];
  world: CollisionWorld;
}

/** Converts a structure-local offset into world space (shared yaw convention). */
export function structureLocalToWorld(
  s: { x: number; z: number; rot: number },
  lx: number,
  lz: number,
): { x: number; z: number } {
  const c = Math.cos(s.rot);
  const n = Math.sin(s.rot);
  return { x: s.x + lx * c + lz * n, z: s.z - lx * n + lz * c };
}

export function structureObstacles(s: StructureDef): Obstacle[] {
  const box = (hw = s.w / 2, hd = s.d / 2): Obstacle => ({ kind: 'box', x: s.x, z: s.z, hw, hd, rot: s.rot });
  switch (s.kind) {
    case 'hall':
    case 'house':
    case 'stall':
    case 'watchtower':
    case 'anvil':
    case 'crate_stack':
    case 'weapon_rack':
    case 'fence':
    case 'palisade':
    case 'shrine':
      return [box()];
    case 'haystack':
    case 'well':
    case 'campfire':
    case 'pond':
      return [{ kind: 'circle', x: s.x, z: s.z, r: s.w / 2 }];
    case 'stone_lantern':
      return [{ kind: 'circle', x: s.x, z: s.z, r: 0.4 }];
    case 'rock':
      return [{ kind: 'circle', x: s.x, z: s.z, r: s.w * 0.45 }];
    case 'tent':
      return [{ kind: 'circle', x: s.x, z: s.z, r: s.w * 0.45 }];
    case 'gate': {
      const a = structureLocalToWorld(s, -s.w / 2, 0);
      const b = structureLocalToWorld(s, s.w / 2, 0);
      return [
        { kind: 'circle', x: a.x, z: a.z, r: 0.45 },
        { kind: 'circle', x: b.x, z: b.z, r: 0.45 },
      ];
    }
    case 'bridge': {
      const a = structureLocalToWorld(s, -s.w / 2, 0);
      const b = structureLocalToWorld(s, s.w / 2, 0);
      return [
        { kind: 'box', x: a.x, z: a.z, hw: 0.2, hd: s.d / 2, rot: s.rot },
        { kind: 'box', x: b.x, z: b.z, hw: 0.2, hd: s.d / 2, rot: s.rot },
      ];
    }
  }
}

export function treeRadius(kind: TreeKind, scale: number): number {
  switch (kind) {
    case 'pine':
      return 0.45 * scale;
    case 'willow':
      return 0.55 * scale;
    case 'maple':
      return 0.5 * scale;
    case 'bamboo':
      return 1.1 * scale;
  }
}

function generateTrees(zone: ZoneDef, base: CollisionWorld): TreeInstance[] {
  const rng = createRng(zone.seed);
  const trees: TreeInstance[] = zone.trees.map((t) => ({ ...t, rot: rng.range(0, Math.PI * 2) }));
  const near = (x: number, z: number, minD: number): boolean =>
    trees.some((t) => {
      const dx = t.x - x;
      const dz = t.z - z;
      return dx * dx + dz * dz < minD * minD;
    });
  const pois: { x: number; z: number; r: number }[] = [
    { x: zone.spawn.x, z: zone.spawn.z, r: 6 },
    ...zone.npcs.map((n) => ({ x: n.x, z: n.z, r: 3 })),
    ...zone.waypoints.map((w) => ({ x: w.x, z: w.z, r: 4 })),
    ...zone.monsterSpawns.map((m) => ({ x: m.x, z: m.z, r: m.radius * 0.5 + 2 })),
  ];

  for (const sc of zone.scatter) {
    let placed = 0;
    const attempts = sc.count * 30;
    for (let a = 0; a < attempts && placed < sc.count; a++) {
      let x: number;
      let z: number;
      if ('r' in sc.region) {
        const ang = rng.range(0, Math.PI * 2);
        const rad = Math.sqrt(rng.next()) * sc.region.r;
        x = sc.region.x + Math.sin(ang) * rad;
        z = sc.region.z + Math.cos(ang) * rad;
      } else {
        x = rng.range(sc.region.minX, sc.region.maxX);
        z = rng.range(sc.region.minZ, sc.region.maxZ);
      }
      const scale = rng.range(sc.minScale, sc.maxScale);
      const rot = rng.range(0, Math.PI * 2);
      const r = treeRadius(sc.kind, scale);
      if (base.overlaps(x, z, r + 1.2)) continue;
      if (zone.roads.some((road) => roadDistance(road.points, x, z) < road.width / 2 + r + 1.2)) continue;
      const pdx = x - zone.plaza.x;
      const pdz = z - zone.plaza.z;
      if (pdx * pdx + pdz * pdz < (zone.plaza.r + 2) ** 2) continue;
      if (zone.fields.some((f) => x > f.minX - 1 && x < f.maxX + 1 && z > f.minZ - 1 && z < f.maxZ + 1))
        continue;
      if (pois.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + r) ** 2)) continue;
      if (near(x, z, sc.spacing * scale)) continue;
      trees.push({ kind: sc.kind, x: round2(x), z: round2(z), scale: round2(scale), rot: round2(rot) });
      placed++;
    }
  }
  return trees;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

export function roadDistance(points: [number, number][], x: number, z: number): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i + 1 < points.length; i++) {
    const a = points[i] as [number, number];
    const b = points[i + 1] as [number, number];
    best = Math.min(best, pointSegmentDistance(x, z, a[0], a[1], b[0], b[1]));
  }
  return best;
}

const cache = new Map<string, ZoneLayout>();

/**
 * Builds (once per zone) the full collision layout: structures, water and the deterministic
 * seeded vegetation. Client and server call this with identical data and get identical results.
 */
export function getZoneLayout(zone: ZoneDef): ZoneLayout {
  const cached = cache.get(zone.id);
  if (cached) return cached;
  const staticObstacles: Obstacle[] = [
    ...zone.structures.flatMap(structureObstacles),
    ...zone.water.map((w): Obstacle => ({ kind: 'box', x: w.x, z: w.z, hw: w.hw, hd: w.hd, rot: w.rot })),
  ];
  const base = new CollisionWorld(zone.bounds, staticObstacles);
  const trees = generateTrees(zone, base);
  const obstacles: Obstacle[] = [
    ...staticObstacles,
    ...trees.map((t): Obstacle => ({ kind: 'circle', x: t.x, z: t.z, r: treeRadius(t.kind, t.scale) })),
  ];
  const layout: ZoneLayout = { zone, obstacles, trees, world: new CollisionWorld(zone.bounds, obstacles) };
  cache.set(zone.id, layout);
  return layout;
}
